import { showError } from "../shared/utils/swalHandler";
import { ROLE } from "../shared/constants/common";
import { UPLOAD_CONFIG } from "../shared/constants/upload";
import {
  uploadLargeFile,
  uploadFileToS3,
  getContentType,
  shouldUseMultipartUpload,
} from "../shared/utils/fileOperations";
import {
  mapTransferProgressToUi,
  mapBundleFileProgressToUi,
} from "../shared/utils/s3DirectUpload";
import { getPresignedUrl } from "../shared/actions/fileActions";
import { getUserRole, isVideo } from "../shared/utils/function";
import { postContent } from "../shared/actions/creatorActions";
import { reportError } from "../shared/sentry";

/**
 * Get video duration from a video file
 * @param {File} videoFile - The file to get duration from
 * @returns {Promise<number>} - Duration in seconds
 */
const getVideoDuration = (videoFile) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(Math.round(video.duration));
    };

    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error("Failed to load video metadata"));
    };

    video.src = URL.createObjectURL(videoFile);
  });
};

/**
 * Update progress bar and invoke callback
 * @param {number} progress - Progress percentage (0-100)
 * @param {function} progressCallback - Optional callback function
 */
const updateProgress = (progress, progressCallback) => {
  const clampedProgress = Math.max(0, Math.min(100, progress));
  const bar = document.getElementById("upload-progress-bar");
  const text = document.getElementById("upload-progress-text");

  if (bar && text) {
    bar.style.width = `${clampedProgress}%`;
    text.textContent = `${clampedProgress}%`;
  }

  if (progressCallback) {
    progressCallback(clampedProgress);
  }
};

/**
 * Wraps raw transfer progress (0–100) into the UI scale (5–95).
 * @param {number} transferPercent
 * @param {function} progressCallback
 */
const reportTransferProgress = (transferPercent, progressCallback) => {
  updateProgress(mapTransferProgressToUi(transferPercent), progressCallback);
};

/**
 * Upload a single file (image or video) with resilient routing and progress reporting.
 * @param {File} file - The file to upload
 * @param {function} onProgress - Callback receiving raw transfer progress (0–100)
 * @returns {Promise<{key: string, duration: number|null, thumbnailUrl: string|null}>}
 */
const uploadSingleFile = async (file, onProgress) => {
  const fileIsVideo = isVideo(file);
  let key;
  let thumbnailUrl = null;

  if (shouldUseMultipartUpload(file)) {
    const result = await uploadLargeFile(file, onProgress, { processImage: false });

    key = result.key;
    thumbnailUrl = result?.thumbnailUrl || null;
  } else {
    const presignedResponse = await getPresignedUrl(file, false);
    if (!presignedResponse.success || !presignedResponse.data) {
      throw new Error(presignedResponse.error || "Failed to get presigned URL");
    }

    const presignedData = presignedResponse.data;
    const fileContentType = getContentType(file);

    await uploadFileToS3(
      presignedData.url,
      file,
      fileContentType,
      onProgress,
      false
    );

    key = presignedData.key;
  }

  let duration = null;
  if (fileIsVideo) {
    try {
      duration = await getVideoDuration(file);
    } catch (error) {
      reportError(error, 'video duration');
    }
  }

  return { key, duration, thumbnailUrl };
};

/**
 * Build request body for content upload
 * @param {object} data - Upload data
 * @param {string} userRole - User role
 * @param {object} fileData - File data (key, duration, etc.)
 * @returns {object} Request body
 */
const buildRequestBody = (data, userRole, fileData = {}) => {
  const requestBody = {
    caption: data.caption,
    lock_content: data.lock_content,
  };

  if (Array.isArray(data.tagIds) && data.tagIds.length > 0) {
    requestBody.tag = data.tagIds;
  }

  if (userRole === ROLE.CREATOR_CONTENT || data?.purpose === "messaging") {
    requestBody.price = data.price;
  }

  if (data?.purpose) {
    requestBody.purpose = data.purpose;
  }

  if (data?.purpose_dest) {
    requestBody.purpose_dest = data.purpose_dest;
  }

  if (fileData.key) {
    requestBody.file = fileData.key;
    requestBody.content_type = data.content_type || (fileData.isVideo ? "video" : "image");

    if (fileData.duration !== null && fileData.duration !== undefined) {
      requestBody.duration = fileData.duration;
    }

    if (fileData.thumbnailUrl) {
      requestBody.poster = fileData.thumbnailUrl;
    }
  }

  if (fileData.files) {
    requestBody.files = fileData.files;
    requestBody.content_type = "bundle";
    if (requestBody?.poster) {
      delete requestBody.poster;
    }
  }

  return requestBody;
};

/**
 * Post content (single file or bundle)
 * @param {object} data - Upload data containing file(s), caption, tags, etc.
 * @param {function} callback - Callback function on completion
 * @param {function} progressCallback - Progress update callback
 */
export const PostContent = (data, callback, progressCallback) => async () => {
  const userRole = getUserRole();

  try {
    if (data.isBundle && data.files) {
      return await handleBundleUpload(data, userRole, callback, progressCallback);
    }

    return await handleSingleFileUpload(data, userRole, callback, progressCallback);
  } catch (error) {
    reportError(error, 'PostContent');
    if (typeof callback === "function") {
      callback("failed");
    }

    const errorMessage = error.message || "Failed to upload content";
    await showError("Error", errorMessage);
  }
};

/**
 * Handle bundle upload (multiple files)
 * @param {object} data - Upload data
 * @param {string} userRole - User role
 * @param {function} callback - Completion callback
 * @param {function} progressCallback - Progress callback
 */
const handleBundleUpload = async (data, userRole, callback, progressCallback) => {
  updateProgress(UPLOAD_CONFIG.PROGRESS.PREPARING_MAX, progressCallback);

  const bundleFiles = [];
  const totalFiles = data.files.length;

  for (let i = 0; i < data.files.length; i++) {
    const fileObj = data.files[i];
    const contentType = fileObj.file.type;

    const fileProgressCallback = (transferPercent) => {
      updateProgress(
        mapBundleFileProgressToUi(i, totalFiles, transferPercent),
        progressCallback
      );
    };

    const { key, duration, thumbnailUrl } = await uploadSingleFile(
      fileObj.file,
      fileProgressCallback
    );

    bundleFiles.push({
      file: key,
      caption: fileObj.caption,
      duration,
      contentType,
      poster: thumbnailUrl || undefined,
    });
  }

  updateProgress(UPLOAD_CONFIG.PROGRESS.FINALIZING_MAX, progressCallback);

  const filesPayload = bundleFiles.map((f) => {
    const item = {
      key: f.file,
      contentType: f.contentType,
      duration: f.duration,
    };

    if (f.poster != null && f.poster !== "") {
      item.poster = f.poster;
    }

    return item;
  });

  const requestBody = buildRequestBody(data, userRole, {
    files: filesPayload,
  });

  const response = await postContent(requestBody);

  if (!response.success) {
    throw new Error(response.error || "Failed to post content");
  }

  const responseData = response.data;

  updateProgress(UPLOAD_CONFIG.PROGRESS.COMPLETE, progressCallback);

  if (typeof callback === "function") {
    callback("success", responseData);
  }

  return responseData;
};

/**
 * Handle single file upload
 * @param {object} data - Upload data
 * @param {string} userRole - User role
 * @param {function} callback - Completion callback
 * @param {function} progressCallback - Progress callback
 */
const handleSingleFileUpload = async (data, userRole, callback, progressCallback) => {
  const fileIsVideo = isVideo(data.file);

  updateProgress(UPLOAD_CONFIG.PROGRESS.PREPARING_MAX, progressCallback);

  const { key, duration, thumbnailUrl } = await uploadSingleFile(
    data.file,
    (transferPercent) => reportTransferProgress(transferPercent, progressCallback)
  );

  updateProgress(UPLOAD_CONFIG.PROGRESS.FINALIZING_MAX, progressCallback);

  const requestBody = buildRequestBody(data, userRole, {
    key,
    duration,
    thumbnailUrl,
    isVideo: fileIsVideo,
  });

  const response = await postContent(requestBody);

  if (!response.success) {
    throw new Error(response.error || "Failed to post content");
  }

  const responseData = response.data;

  updateProgress(UPLOAD_CONFIG.PROGRESS.COMPLETE, progressCallback);

  if (typeof callback === "function") {
    callback("success", responseData);
  }

  return responseData;
};
